import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgEye, CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { isWebpFile } from "../../utils/fileValidation";
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";
import ListHeader from "../Commaon/ListHeader";
import ListPagination from "../Commaon/ListPagination";
import LabTechnicianDetailsModal, {
  formatRadius,
  formatServiceLocation,
  getLabDetails,
} from "./LabTechnicianDetailsModal";

const getRows = (data) => {
  if (Array.isArray(data?.r)) return data.r;
  if (Array.isArray(data?.r?.data)) return data.r.data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const toInputValue = (value) => (value === null || value === undefined ? "" : String(value));

const LabTechniciansList = () => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [viewData, setViewData] = useState(null);
  const [basicModal, setBasicModal] = useState(false);
  const [labModal, setLabModal] = useState(false);
  const [educationModal, setEducationModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteIds, setDeleteIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [basicForm, setBasicForm] = useState({
    agentId: "",
    fullName: "",
    mobileNumber: "",
    email: "",
    dob: "",
    age: "",
    alterMobileNumber: "",
    address1: "",
    address2: "",
    pincode: "",
  });
  const [labForm, setLabForm] = useState({
    agentId: "",
    labName: "",
    licenceNumber: "",
    address1: "",
    address2: "",
    landmark: "",
    pincode: "",
    town: "",
    city: "",
    latitude: "",
    longitude: "",
  });
  const [educationForm, setEducationForm] = useState({
    agentId: "",
    higherEducation: "",
    degreeName: "",
    passingYear: "",
    images: null,
  });

  const showSnack = (message, type = "success") => {
    setSnack({ notification: { message, type } });
  };

  const loadTechnicians = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/lab-technician?page=${pageNo}&limit=${limit}`,
        { method: "GET" }
      );
      setTechnicians(getRows(response.data));
      setTotalRecords(response?.data?.tr || 0);
    } catch (error) {
      setTechnicians([]);
      setTotalRecords(0);
      showSnack("Error fetching lab technicians", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTechnicians();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, limit]);

  const filteredTechnicians = useMemo(() => {
    if (!query.trim()) return technicians;
    const search = query.toLowerCase();
    return technicians.filter(
      (item) =>
        item.fullName?.toLowerCase().includes(search) ||
        item.email?.toLowerCase().includes(search) ||
        item.mobileNumber?.toLowerCase().includes(search) ||
        item.labName?.toLowerCase().includes(search) ||
        item.labId?.toLowerCase().includes(search)
    );
  }, [query, technicians]);
  const totalPages = Math.ceil(totalRecords / limit) || 1;
  const visibleIds = filteredTechnicians.map((item) => item._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const runBulkStatus = async (action) => {
    if (!selectedIds.length) {
      showSnack("Select at least one lab technician", "error");
      return;
    }
    const isActive = action === "activate";
    // The status API toggles, so only call it for technicians not already in the target state.
    const ids = selectedIds.filter(
      (id) => technicians.find((item) => item._id === id)?.isActive !== isActive
    );
    setLoading(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => AuthInterceptor(`/admin/lab-technician/${id}`, { method: "PATCH" }))
      );
      const failed = results.filter((r) => r.status !== "fulfilled" || r.value?.data?.s !== 200).length;
      if (failed) {
        showSnack(`Updated ${ids.length - failed} of ${ids.length} lab technicians`, "error");
      } else {
        showSnack("Bulk action completed");
      }
      setSelectedIds([]);
      loadTechnicians();
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (ids) => {
    if (!ids.length) {
      showSnack("Select at least one lab technician", "error");
      return;
    }
    setDeleteIds(ids);
    setDeleteModal(true);
  };

  const deleteTechnicians = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/lab-technician", {
        method: "DELETE",
        body: JSON.stringify({ ids: deleteIds }),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        showSnack(response?.data?.m || "Lab technicians deleted successfully");
        setSelectedIds((prev) => prev.filter((id) => !deleteIds.includes(id)));
        setDeleteModal(false);
        loadTechnicians();
      } else {
        showSnack(response?.data?.m || "Error deleting lab technicians", "error");
      }
    } catch (error) {
      showSnack("Error deleting lab technicians", "error");
    } finally {
      setLoading(false);
      setDeleteIds([]);
    }
  };

  const getTechnicianDetails = async (id) => {
    const response = await AuthInterceptor(`/admin/lab-technician/${id}`, {
      method: "GET",
    });
    return response?.data?.r || response?.data?.data || {};
  };

  const openView = async (id) => {
    setLoading(true);
    try {
      setViewData(await getTechnicianDetails(id));
    } catch (error) {
      showSnack("Error loading technician details", "error");
    } finally {
      setLoading(false);
    }
  };

  const updateTechnician = async (id, action) => {
    const url =
      action === "approve"
        ? `/admin/lab-technician/approve/${id}`
        : `/admin/lab-technician/${id}`;
    setLoading(true);
    try {
      const response = await AuthInterceptor(url, { method: "PATCH" });
      if (response?.data?.s === 200 || response?.status === 200) {
        showSnack(`Technician ${action === "approve" ? "approval" : "status"} updated`);
        loadTechnicians();
      } else {
        showSnack(response?.data?.m || "Error updating technician", "error");
      }
    } catch (error) {
      showSnack("Error updating technician", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitBasic = async (event) => {
    event.preventDefault();
    const body = { ...basicForm, age: basicForm.age ? Number(basicForm.age) : "" };
    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/lab-technician/register-basic", {
        method: "POST",
        body: JSON.stringify(body),
      });
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        showSnack("Basic details saved successfully");
        setBasicModal(false);
        loadTechnicians();
      } else {
        showSnack(response?.data?.m || "Error saving basic details", "error");
      }
    } catch (error) {
      showSnack("Error saving basic details", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitLab = async (event) => {
    event.preventDefault();
    if (!labForm.agentId) {
      showSnack("Select a technician from the list first", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/lab-technician/register-lab", {
        method: "POST",
        body: JSON.stringify(labForm),
      });
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        showSnack("Laboratory details saved successfully");
        setLabModal(false);
        loadTechnicians();
      } else {
        showSnack(response?.data?.m || "Error saving laboratory details", "error");
      }
    } catch (error) {
      showSnack("Error saving laboratory details", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitEducation = async (event) => {
    event.preventDefault();
    if (!educationForm.agentId) {
      showSnack("Select a technician from the list first", "error");
      return;
    }
    const formData = new FormData();
    formData.append("agentId", educationForm.agentId);
    formData.append("higherEducation", educationForm.higherEducation);
    formData.append("degreeName", educationForm.degreeName);
    formData.append("passingYear", educationForm.passingYear);
    const images = Array.from(educationForm.images || []);
    if (images.some((file) => !isWebpFile(file))) {
      showSnack("Only WebP, PNG, JPG, and JPEG image files are allowed", "error");
      return;
    }
    images.forEach((file) => {
      formData.append("images", file);
    });

    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/lab-technician/register-education", {
        method: "POST",
        body: formData,
      });
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        showSnack("Education details saved successfully");
        setEducationModal(false);
        loadTechnicians();
      } else {
        showSnack(response?.data?.m || "Error saving education details", "error");
      }
    } catch (error) {
      showSnack("Error saving education details", "error");
    } finally {
      setLoading(false);
    }
  };

  const openForAgent = async (row, modal) => {
    const agentId = row?._id || "";
    if (!agentId) {
      setBasicForm({
        agentId: "", fullName: "", mobileNumber: "", email: "", dob: "", age: "",
        alterMobileNumber: "", address1: "", address2: "", pincode: "",
      });
      setBasicModal(true);
      return;
    }

    setLoading(true);
    try {
      const details = await getTechnicianDetails(agentId);
      if (modal === "basic") {
        setBasicForm({
          agentId,
          fullName: details.fullName || "",
          mobileNumber: details.mobileNumber || "",
          email: details.email || "",
          dob: details.dob ? String(details.dob).slice(0, 10) : "",
          age: details.age ?? "",
          alterMobileNumber: details.alterMobileNumber || "",
          address1: details.address1 || "",
          address2: details.address2 || "",
          pincode: details.pincode || "",
        });
        setBasicModal(true);
      }
      if (modal === "lab") {
        setLabForm({
          agentId,
          labName: details.lab?.labName || "",
          licenceNumber: details.lab?.licenceNumber || "",
          address1: details.lab?.address1 || "",
          address2: details.lab?.address2 || "",
          landmark: details.lab?.landmark || "",
          pincode: details.lab?.pincode || "",
          town: details.lab?.town || "",
          city: details.lab?.city || "",
          latitude: toInputValue(details.lab?.latitude),
          longitude: toInputValue(details.lab?.longitude),
        });
        setLabModal(true);
      }
      if (modal === "education") {
        setEducationForm({
          agentId,
          higherEducation: details.educationDetails?.higherEducation || "",
          degreeName: details.educationDetails?.degreeName || "",
          passingYear: details.educationDetails?.passingYear || "",
          images: null,
        });
        setEducationModal(true);
      }
    } catch (error) {
      showSnack("Error loading technician details", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <ListHeader
          title="Lab Technicians"
          searchValue={query}
          searchPlaceholder="Search lab technicians..."
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => {
            setPageNo(1);
            setLimit(size);
          }}
        />

        {/* Bulk actions | Register */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {/* Bulk actions only make sense when there are rows to select */}
          {filteredTechnicians.length > 0 && (
            <>
              <span className="text-muted small">{selectedIds.length} selected</span>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkStatus("activate")}>
                Active
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkStatus("inactive")}>
                Inactive
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={() => openDeleteModal(selectedIds)}>
                Delete
              </button>
            </>
          )}
          <button className="btn btn-sm btn-outline-custom ms-auto" onClick={() => openForAgent(null, "basic")}>
            Register Basic
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all lab technicians on this page"
                  />
                </th>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>Lab ID</th>
                <th>Name</th>
                <th>Lab Name</th>
                <th>Mobile</th>
                <th>Town / Village</th>
                <th>Radius</th>
                <th>Total Orders</th>
                <th>Approval</th>
                <th>Status</th>
                <th style={{ width: "310px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTechnicians.length > 0 ? (
                filteredTechnicians.map((item, index) => (
                  <tr key={item._id || index}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(item._id)}
                        onChange={() => toggleRowSelection(item._id)}
                        aria-label={`Select ${item.fullName || "lab technician"}`}
                      />
                    </td>
                    <td>{(pageNo - 1) * limit + index + 1}</td>
                    <td className="fw-semibold">{item.labId || "-"}</td>
                    <td>{item.fullName || item.name || "-"}</td>
                    <td>{item.labName || item.lab?.labName || "-"}</td>
                    <td>{item.mobileNumber || "-"}</td>
                    <td>
                      <div>{getLabDetails(item).town || "-"}</div>
                      <div className="text-muted small">{formatServiceLocation(item)}</div>
                    </td>
                    <td>{formatRadius(item)}</td>
                    <td>{item.totalOrders ?? 0}</td>
                    <td>
                      <span className={`badge ${item.isApproved ? "bg-success" : "bg-warning text-dark"}`}>
                        {item.isApproved ? "Completed" : "Pending"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${item.isActive ? "bg-success" : "bg-secondary"}`}
                        role="button"
                        onClick={() => updateTechnician(item._id, "status")}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        <button className="btn btn-sm btn-outline-info" onClick={() => openView(item._id)}>
                          <CgEye size={16} />
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => updateTechnician(item._id, "status")}>
                          Status
                        </button>
                        <button className="btn btn-sm btn-outline-custom" onClick={() => updateTechnician(item._id, "approve")}>
                          Approve
                        </button>
                        <button className="btn btn-sm btn-outline-custom" onClick={() => openForAgent(item, "basic")}>
                          Basic
                        </button>
                        <button className="btn btn-sm btn-outline-custom" onClick={() => openForAgent(item, "lab")}>
                          Lab
                        </button>
                        <button className="btn btn-sm btn-outline-custom" onClick={() => openForAgent(item, "education")}>
                          Education
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => openDeleteModal([item._id])}>
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
                ) : (
                <tr>
                  <td colSpan="12" className="text-center text-muted py-4">
                    No lab technicians found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          show={totalRecords > 0}
          pageNo={pageNo}
          totalPages={totalPages}
          onPageChange={setPageNo}
        />
      </div>

      <LabTechnicianDetailsModal technician={viewData} onHide={() => setViewData(null)} />

      <Modal show={basicModal} onHide={() => setBasicModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{basicForm.agentId ? "Edit Basic Details" : "Register Basic"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitBasic}>
            <div className="row">
              {Object.keys(basicForm).filter((name) => name !== "agentId").map((name) => (
                <div className="col-md-6 mb-3" key={name}>
                  <label className="form-label">{name}</label>
                  <input className="form-control" type={name === "dob" ? "date" : "text"} value={basicForm[name]} onChange={(e) => setBasicForm((prev) => ({ ...prev, [name]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setBasicModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-outline-custom">Save</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={labModal} onHide={() => setLabModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Laboratory Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitLab}>
            {Object.keys(labForm).filter((name) => name !== "agentId").map((name) => (
              <div className="mb-3" key={name}>
                <label className="form-label">{name}</label>
                <input className="form-control" value={labForm[name]} onChange={(e) => setLabForm((prev) => ({ ...prev, [name]: e.target.value }))} />
              </div>
            ))}
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setLabModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-outline-custom">Save</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={educationModal} onHide={() => setEducationModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Education Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitEducation}>
            {["higherEducation", "degreeName", "passingYear"].map((name) => (
              <div className="mb-3" key={name}>
                <label className="form-label">{name}</label>
                <input className="form-control" value={educationForm[name]} onChange={(e) => setEducationForm((prev) => ({ ...prev, [name]: e.target.value }))} />
              </div>
            ))}
            <div className="mb-3">
              <label className="form-label">images</label>
              <input className="form-control" type="file" accept="image/webp,image/png,image/jpeg" multiple onChange={(e) => setEducationForm((prev) => ({ ...prev, images: e.target.files }))} />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEducationModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-outline-custom">Save</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <DeleteConfirmModal
        show={deleteModal}
        count={deleteIds.length}
        singular="lab technician"
        plural="lab technicians"
        loading={loading}
        onConfirm={deleteTechnicians}
        onCancel={() => setDeleteModal(false)}
      />
    </>
  );
};

export default LabTechniciansList;
